// filepath: apps/web/src/features/promote/useRejectPromotion.ts
//
// FR-UI-PROM-2 AC-4 — TanStack Query mutation for
// POST /api/admin/promotion-reviews/{id}/reject.
//
// Mirrors useApprovePromotion.ts. The reason is captured by the
// RejectReasonDialog and stored on the promotion_reviews row's
// metadata by the existing serveReject handler (FR-PROM-3, no
// backend change). The cache is invalidated on success so the
// caller re-renders the now-rejected row.
//
// FR-PROM-3 wire status-code mapping:
//   - 200 OK → success → invalidate review context
//   - 401    → typed auth error (caller surfaces login UI)
//   - 404    → typed not-found (review does not exist for tenant)
//   - 410    → typed terminal-state (already approved/applied/rejected)
//   - other  → typed network/HTTP error
//
// INV-7: the hook does NOT retry on 409 — the reviewer must re-load
// the review and decide again. Matches FR-UI-CORE-6 conflict UX.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import { promoteKeys } from "./usePromotionReview";

interface RejectVariables {
  reviewId: string;
  reason: string;
}

export function useRejectPromotion() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, reason }: RejectVariables) => {
      return apiFetch(`/api/admin/promotion-reviews/${reviewId}/reject`, {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason,
            idempotency_key: crypto.randomUUID(),
          }),
        },
        idempotencyKey: true,
      }).then(async (r) => {
        if (!r.ok) {
          // Consume the body once; failures don't use it but the
          // network socket must be drained.
          await r.text().catch(() => null);
          throw new ApiError(r.statusText, r.status, `http_${r.status}`);
        }
        return r.json() as Promise<unknown>;
      });
    },
    onSuccess: (_data: unknown, variables: RejectVariables) => {
      queryClient.invalidateQueries({
        queryKey: promoteKeys.reviewContext(tenantSlug, variables.reviewId),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}
